import { useTranslation } from "react-i18next";
import type { GovernanceArtifactItem } from "@/hooks/useGovernanceArtifacts";
import { displayLabel } from "@/lib/governanceTree";
import { Badge } from "@/components/ui/badge";

interface ArtifactViewerProps {
  artifact: GovernanceArtifactItem;
}

/**
 * Read-only detail pane for a governance artifact (SD-9).
 * Displayed inline below the tree -- not a sheet/drawer.
 */
export function ArtifactViewer({ artifact }: ArtifactViewerProps) {
  const { t } = useTranslation();
  const filename = artifact.path.split("/").pop() ?? "";
  const label = displayLabel(filename);
  const badgeText =
    artifact.type === "adr"
      ? t("projectPortal.governance.badge.adr")
      : t("projectPortal.governance.badge.governance");

  return (
    <div
      className="rounded-lg border p-4 space-y-3"
      data-testid="artifact-viewer"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">{label}</h3>
        <Badge variant={artifact.type === "adr" ? "default" : "secondary"}>
          {badgeText}
        </Badge>
      </div>

      <div>
        <span className="text-sm text-muted-foreground">
          {t("projectPortal.governance.viewer.path")}
        </span>
        <p className="font-mono text-sm">{artifact.path}</p>
      </div>

      {artifact.content_summary && (
        <div>
          <span className="text-sm text-muted-foreground">
            {t("projectPortal.governance.viewer.summary")}
          </span>
          <p className="text-sm">{artifact.content_summary}</p>
        </div>
      )}

      <div>
        <span className="text-sm font-medium">
          {t("projectPortal.governance.viewer.provenance")}
        </span>
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">
            {t("projectPortal.governance.viewer.repository")}
          </dt>
          <dd>{artifact.provenance.repo_url}</dd>

          <dt className="text-muted-foreground">
            {t("projectPortal.governance.viewer.branch")}
          </dt>
          <dd>{artifact.provenance.branch}</dd>

          <dt className="text-muted-foreground">
            {t("projectPortal.governance.viewer.commit")}
          </dt>
          <dd>{artifact.provenance.commit_sha.slice(0, 8)}</dd>

          <dt className="text-muted-foreground">
            {t("projectPortal.governance.viewer.author")}
          </dt>
          <dd>{artifact.provenance.author ?? "-"}</dd>

          <dt className="text-muted-foreground">
            {t("projectPortal.governance.viewer.synced")}
          </dt>
          <dd>
            {new Date(artifact.synced_at).toLocaleDateString()}
          </dd>
        </dl>
      </div>
    </div>
  );
}
